/* eslint-disable */
/* eslint-disable arrow-parens */
import { useState, useCallback, useEffect, useContext, useRef, Suspense } from "react";
import { Button, IconButton, Tooltip, Checkbox, Grid } from "@material-ui/core";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import { trackPromise } from "react-promise-tracker";
import ImportExportIcon from "@material-ui/icons/ImportExport";
import { withStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import { ColumnApi, GridReadyEvent } from "ag-grid-community";
import ROLES from "../../../../../constants/roles";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import { handleDateCellRenderer } from "../../../../../utils/commonFunctions/cellRenderers";
import { LazyCustomDialog, LazyCustomWaivedBenifitDialog } from "../../../../shared";
import { AuthContext, GroupContext, UIContext } from "../../../../../contexts";
import getAgGridHeight from "../../../../../utils/commonFunctions/getAgGridHeight";
import initCapitalize from "../../../../../utils/commonFunctions/initCapitalize";
import { api } from "../../../../../utils/api";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { dateTimeZoneConverter } from "../../../../../utils/commonFunctions/date";
import { TERM_REASONS } from "../../../../../constants/termReasons";
import {
	CurrentEnrollment,
	CustomWaivedBenifitDetails,
	CustomWaivedDialogProps,
	WaivedEnrollmentDetails
} from "../../../../../@types/waivedBenifits.types";
import { Validation } from "../../../../../@types/validation.types";
import { WaivedBenifits } from "../../../../../utils/api/enrollment/getwaivedBenefits";
import { ApproveWaivedEnrollmentPayload } from "../../../../../utils/api/enrollment/enrollment";

// icons and assets
import VisibilityIcon from "@material-ui/icons/Visibility";
import CancelIcon from "@material-ui/icons/Cancel";

// styling
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";

const GreenCheckbox = withStyles({
	root: {
		color: green[400],
		"&$checked": {
			color: green[600]
		}
	},
	onClick: {},
	checked: {}
})((props) => <Checkbox color="default" {...props} size="small" />);

const WaivedBenefitsGrid = () => {
	const [waivedBenefitDetails, setWaivedBenefitDetails] = useState<CustomWaivedBenifitDetails[]>([]);
	const [customDialogProps, setCustomDialogProps] = useState({
		openDialog: false,
		title: "",
		content: "",
		actions: []
	});
	const [gridHeight, setGridHeight] = useState("400px");
	const [isReadyForExport, setIsReadyForExport] = useState(false);
	const [agGridAPI, setAgGridAPI] = useState(null);
	const [agGridColumnAPI, setAgGridColumnAPI] = useState<ColumnApi | null>(null);
	const { currentGroup, setCurrentGroup } = useContext(GroupContext);
	const [waivedDetails, setWaivedDetails] = useState<WaivedEnrollmentDetails>({
		termination_date: "",
		termination_reason: "Member Waived"
	});
	const [currentEnrollment, setCurrentEnrollment] = useState<CurrentEnrollment[]>([
		{ label: "Plan Name", name: "plan_name", value: "" },
		// { label: "Enrollment Date", name: "enrollment_date", value: "" },
		// { label: "Effective Date", name: "effective_date", value: "" },
		{ label: "Initial Effective Date", name: "initial_effective_date", value: "" },
		{ label: "Total Premium", name: "total_premium", value: null }
	]);
	const [waivedValidation, setWaivedValidation] = useState<Validation>({
		waivedDetails: {},
		status: "invalid"
	});
	const [customWaivedDialogProps, setCustomWaivedDialogProps] = useState<CustomWaivedDialogProps>({
		openDialog: false,
		title: "Do you want to approve the waived enrollment?",
		waivedDetails: null,
		inputFields: [
			{
				label: "Termination Date",
				placeholder: "Enter Termination Date",
				name: "termination_date",
				value: "",
				type: "date"
			},
			{
				label: "Termination Reason",
				placeholder: "Enter Termination Reason",
				name: "termination_reason",
				value: "",
				type: "select",
				options: TERM_REASONS
			}
		],
		actions: [
			{
				label: "Reject",
				name: "reject",
				callback: () => null
			},
			{
				label: "Approve",
				name: "approve",
				callback: () => null
			}
		]
	});
	const [pagination, setPagination] = useState({
		currentPage: 1,
		pageCount: 2
	});
	const queryGroupNumber = useRouteParam("gn");
	const _termValidation = useRef<Validation>();
	const agGridRef = useRef<any>(null);
	const { setDashboardHeader } = useContext(UIContext);
	const { user } = useContext(AuthContext);
	const { currentPage, pageCount } = pagination;

	// const handleTerminationDateChange = useCallback(
	// 	(_date: Date, name: string) => {
	// 		const date = new Date(_date);
	// 		const parsedDate = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();
	// 		setWaivedDetails(Object.assign({}, waivedDetails, { [name]: parsedDate }));
	// 	},
	// 	[setWaivedDetails, waivedDetails]
	// );
	const handleTerminationDateChange = useCallback(
		(_date: Date, name: string) => {
			if (!_date) {
				setWaivedDetails((prev) => ({ ...prev, [name]: "" }));
				return;
			}
			const date = new Date(_date);
			if (isNaN(date.getTime())) {
				setWaivedDetails((prev) => ({ ...prev, [name]: "" }));
				return;
			}
			const parsedDate = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();
			setWaivedDetails((prev) => ({ ...prev, [name]: parsedDate }));
		},
		[] // no deps needed
	);

	const handleTerminationReasonChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { value } = event.target;
			const { name } = event.target;
			setWaivedDetails(Object.assign({}, waivedDetails, { [name]: value }));
		},
		[setWaivedDetails, waivedDetails]
	);

	const getCurrentBenefitById = useCallback(async () => {
		const focusedCell = agGridRef.current.api.getFocusedCell();
		const row = agGridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
		const { data } = row;
		const { _id } = data;

		const response = await trackPromise(api.enrollments.getCurrentBenefitById(_id));

		if (response) {
			const _currentBenefits: CurrentEnrollment[] = currentEnrollment;
			const { plan_name, enrollment_date, effective_date, initial_effective_date, total_premium } = response;
			const formattedTotalPremium = `$${total_premium.toFixed(2)}`;

			_currentBenefits[0].value = plan_name;
			// _currentBenefits[1].value = dateTimeZoneConverter(enrollment_date);
			// _currentBenefits[2].value = dateTimeZoneConverter(effective_date);
			_currentBenefits[1].value = dateTimeZoneConverter(initial_effective_date);
			_currentBenefits[2].value = formattedTotalPremium;

			setCurrentEnrollment(Object.assign([], _currentBenefits));
		}
	}, [currentEnrollment]);

	const getMemberWaivedBenefitDetails = useCallback(async (queryGroupNumber: number) => {
		const _waivedBenefitDetails: CustomWaivedBenifitDetails[] = [];
		const company = "nex";
		const waivedBenefits: WaivedBenifits[] = await trackPromise(
			api.enrollments.getMemberWaivedBenefits(company, queryGroupNumber)
		);

		if (waivedBenefits) {
			waivedBenefits.forEach((waivedBenefit: WaivedBenifits) => {
				const {
					_id,
					plan_name,
					plan_code,
					employee,
					enrollment_status,
					group_number,
					location_number,
					enrollment_date,
					action_taken_against_waive,
					insured_SSN
				} = waivedBenefit;

				_waivedBenefitDetails.push({
					_id,
					plan_name,
					plan_code,
					employee_name: employee.first_name + " " + employee.last_name,
					location_number,
					member_id: employee.member_id,
					insured_SSN,
					enrollment_status: initCapitalize(enrollment_status),
					enrollment_date,
					group_number,
					action_taken_against_waive
				});
			});
		}
		setWaivedBenefitDetails(Object.assign([], _waivedBenefitDetails));
	}, []);

	const handleCloseCustomWaiveDialogClick = useCallback(() => {
		const _termDetails = {
			termination_date: "",
			termination_reason: ""
		};
		setWaivedDetails(Object.assign({}, _termDetails));

		_termValidation.current = {
			waivedDetails: {
				termination_date: "",
				termination_reason: ""
			},
			status: "valid"
		};
		setWaivedValidation(Object.assign({}, _termValidation.current));

		setCustomWaivedDialogProps(Object.assign({}, customWaivedDialogProps, { openDialog: false }));
	}, [customWaivedDialogProps]);

	const handleWaivedEnrollmentValidation = useCallback(async () => {
		const { termination_date, termination_reason } = waivedDetails;

		_termValidation.current = {
			...waivedValidation,
			waivedDetails: {},
			status: "invalid"
		};
		let flag = true;

		if (termination_reason.length === 0) {
			_termValidation.current.waivedDetails["termination_reason"] = "Termination Reason is required";
			_termValidation.current["status"] = "invalid";
			flag = false;
			// console.log("termination reason is invalid -- validation fires");
		}

		if (String(termination_date).length === 0) {
			_termValidation.current.waivedDetails["termination_date"] = "Termination Date is required";
			_termValidation.current["status"] = "invalid";
			flag = false;
		}

		if (flag === true) _termValidation.current["status"] = "valid";
		else {
			_termValidation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setWaivedValidation(Object.assign({}, _termValidation.current));
		return _termValidation.current["status"];
	}, [waivedDetails, waivedValidation]);

	const handleAgGridReady = (params: GridReadyEvent) => {
		const { api, columnApi } = params;
		setAgGridAPI(Object.assign({}, agGridAPI, api));
		setAgGridColumnAPI(columnApi);
	};

	const handleFirstDataRendered = (params: any) => {
		params.api.sizeColumnsToFit();
		params.columnApi.setColumnWidth("action", "100%", true);
		const { api, columnApi } = params;
		api.sizeColumnsToFit();
	};

	const handlePageChange = useCallback(
		(event: Event, value: any) => {
			setPagination(Object.assign({}, pagination, { currentPage: value }));
		},
		[pagination]
	);

	const handleExportButtonClick = () => {
		agGridRef?.current?.api.exportDataAsCsv({
			fileName: "Enrollment Approvals.csv",
			processCellCallback: (params: any) => {
				// console.log("params", params);
				switch (params.column.colId) {
					case "employee": {
						return params.value.first_name + " " + params.value.last_name;
					}
					case "enrollment_date": {
						return dateTimeZoneConverter(params.value);
					}
					default:
						return params.value;
				}
			}
		});
	};

	const handleSSNCellRenderer = ({ value }: any) => {
		const SSN = value.substring(0, 3) + "-" + value.substring(3, 5) + "-" + value.substring(5);
		return <div>{SSN}</div>;
	};

	// wavive dialog - open functionality
	// const handleOpenWavedDialogClick = useCallback(
	// 	(waivedDetails: WaivedEnrollmentDetails) => {
	// 		getCurrentBenefitById();
	// 		setCustomWaivedDialogProps(
	// 			Object.assign({}, customWaivedDialogProps, { openDialog: true, waivedDetails: waivedDetails })
	// 		);
	// 	},
	// 	[customWaivedDialogProps, getCurrentBenefitById]
	// );
	const handleOpenWavedDialogClick = useCallback(
		(waivedDetails: WaivedEnrollmentDetails) => {
			getCurrentBenefitById();

			// Reset so previous row's date never bleeds in
			setWaivedDetails({
				termination_date: "",
				termination_reason: "Member Waived"
			});

			setCustomWaivedDialogProps((prev) => ({
				...prev,
				openDialog: true,
				waivedDetails,
				inputFields: prev.inputFields.map((field) =>
					field.name === "termination_date"
						? { ...field, value: "" }
						: field.name === "termination_reason"
						? { ...field, value: "Member Waived" }
						: field
				)
			}));
		},
		[getCurrentBenefitById]
	);
	// wavive dialog - close functionality
	const handleCloseCustomWaivedDialogClick = useCallback(() => {
		const _waivedDetials = {
			termination_date: "",
			termination_reason: "Member Waived"
		};

		setWaivedDetails(Object.assign({}, _waivedDetials));

		_termValidation.current = {
			waivedDetails: {
				termination_date: "",
				termination_reason: ""
			},
			status: "valid"
		};
		setWaivedValidation(Object.assign({}, _termValidation.current));

		setCustomWaivedDialogProps(
			Object.assign({}, customWaivedDialogProps, {
				openDialog: false
			})
		);
	}, [customWaivedDialogProps]);

	const handleCloseCustomDialogClick = useCallback(() => {
		setCustomDialogProps(
			Object.assign({}, customDialogProps, {
				openDialog: false
			})
		);
	}, [customDialogProps]);

	const checkedActionAgainstWaiveClick = useCallback(async () => {
		const focusedCell = agGridRef.current.api.getFocusedCell();
		const row = agGridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
		const { data } = row;
		const { _id, group_number, action_taken_against_waive } = data;
		const company = "nex";
		const waivedBenefitPayload = {
			waivedActionValue: true
		};
		if (!action_taken_against_waive) {
			setCustomDialogProps(
				Object.assign({}, customDialogProps, {
					openDialog: true,
					title: "Do you want to remove this waived benefit ?",
					content: "Click Yes to proceed, otherwise Cancel",
					actions: [
						{
							label: "Cancel",
							name: "cancel",
							callback: () => {
								handleCloseCustomDialogClick();
							}
						},
						{
							label: "Yes",
							name: "submit",
							callback: async () => {
								const response = await trackPromise(
									api.enrollments.editActionTakenAgainstWaive(
										_id,
										company,
										group_number,
										waivedBenefitPayload
									)
								);
								if (response) {
									getMemberWaivedBenefitDetails(group_number);
								}
								handleCloseCustomDialogClick();
							}
						}
					]
				})
			);
		} else {
			alert("Action has already been taken for this waived benefit!");
		}
	}, [customDialogProps, getMemberWaivedBenefitDetails, handleCloseCustomDialogClick]);

	const handleApproveWaivedEnrollmentClick = useCallback(
		async (approvalStatus: boolean, waivedDetails: WaivedEnrollmentDetails) => {
			try {
				const waivedValidation = await handleWaivedEnrollmentValidation(); // calling the validation function and checking the fields for the validation

				if (waivedValidation === "valid") {
					const focusedCell = agGridRef.current.api.getFocusedCell();
					const row = agGridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
					const { data } = row;
					const { _id, group_number, plan_code } = data;
					const company = "nex";
					const { termination_date, termination_reason } = waivedDetails;

					if (termination_date != null && termination_reason != null) {
						let approveWaivedEnrollmentPayload: ApproveWaivedEnrollmentPayload | null = null;

						if (approvalStatus === false) {
							approveWaivedEnrollmentPayload = {
								_id,
								group_number,
								plan_code,
								approval_status: approvalStatus
							};
						}

						if (approvalStatus === true) {
							approveWaivedEnrollmentPayload = {
								_id,
								group_number,
								plan_code,
								approval_status: approvalStatus,
								termination_date,
								termination_reason
							};
						}

						if (approveWaivedEnrollmentPayload != null) {
							const waivedBenefits = await api.enrollments.approveWaivedEnrollment(
								approveWaivedEnrollmentPayload
							);

							if (waivedBenefits) {
								const _waivedBenefitDetails: CustomWaivedBenifitDetails[] = [];
								waivedBenefits.forEach((eachWaivedBenefit: WaivedBenifits) => {
									const {
										_id,
										plan_name,
										plan_code,
										employee,
										enrollment_status,
										group_number,
										location_number,
										enrollment_date,
										action_taken_against_waive,
										insured_SSN
									} = eachWaivedBenefit;

									_waivedBenefitDetails.push({
										_id,
										plan_name,
										plan_code,
										employee_name: employee.first_name + " " + employee.last_name,
										location_number,
										member_id: employee.member_id,
										insured_SSN,
										enrollment_status: initCapitalize(enrollment_status),
										enrollment_date,
										group_number,
										action_taken_against_waive
									});
								});

								setWaivedBenefitDetails(Object.assign([], _waivedBenefitDetails));
							}

							const approvalMessage = approvalStatus ? "Approved" : "Rejected";
							alert(`Enrollment for this member has been succesfully ${approvalMessage}`);
							handleCloseCustomWaivedDialogClick();
						}
					}
				}
			} catch (error) {
				throw error;
			}
		},
		[handleCloseCustomWaivedDialogClick, handleWaivedEnrollmentValidation]
	);

	const handleApproveWaivedEnrollmentActionCellRenderer = useCallback(
		({ value, waivedDetails }: any) => {
			if (value.toUpperCase() === "WAIVED") {
				return (
					<Grid container>
						<Grid item>
							<Button
								className="ag-grid-cell-button"
								variant="outlined"
								onClick={checkedActionAgainstWaiveClick}
							>
								Take Action
							</Button>
						</Grid>
					</Grid>
				);
			} else if (value.toUpperCase() === "WAIVED - WAITING FOR APPROVAL") {
				return (
					<Grid container>
						<Grid item>
							<Button
								className="ag-grid-cell-button"
								variant="outlined"
								onClick={() => handleOpenWavedDialogClick(waivedDetails)}
							>
								Take Action
							</Button>
						</Grid>
					</Grid>
				);
			}
		},
		[checkedActionAgainstWaiveClick, handleOpenWavedDialogClick]
	);

	const handleActionCellRender = () => {
		return (
			<>
				<Tooltip title="View" leaveDelay={200} enterDelay={500} arrow>
					<IconButton>
						<VisibilityIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Clear" leaveDelay={200} enterDelay={500} arrow>
					<IconButton>
						<CancelIcon />
					</IconButton>
				</Tooltip>
			</>
		);
	};

	// useEffect(() => {
	// 	if (customWaivedDialogProps.openDialog === true) {
	// 		setCustomWaivedDialogProps((prevCustomWaivedDialogProps) => {
	// 			prevCustomWaivedDialogProps.inputFields[0].value = waivedDetails.termination_date;
	// 			prevCustomWaivedDialogProps.inputFields[1].value = waivedDetails.termination_reason;

	// 			return Object.assign({}, prevCustomWaivedDialogProps);
	// 		});
	// 	}
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [waivedDetails]);
	useEffect(() => {
		if (customWaivedDialogProps.openDialog === true) {
			setCustomWaivedDialogProps((prev) => ({
				...prev,
				inputFields: prev.inputFields.map((field) => {
					if (field.name === "termination_date") {
						return { ...field, value: waivedDetails.termination_date };
					}
					if (field.name === "termination_reason") {
						return { ...field, value: waivedDetails.termination_reason };
					}
					return field;
				})
			}));
		}
	}, [waivedDetails]);
	useEffect(() => {
		if (queryGroupNumber) getMemberWaivedBenefitDetails(queryGroupNumber as number);
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.employer_waived_benefit);
		// for export button and setting up the height of the grid
		if (waivedBenefitDetails.length !== 0) setIsReadyForExport(true);
		const _gridHeight = getAgGridHeight(waivedBenefitDetails.length);
		setGridHeight(_gridHeight);
	}, [getMemberWaivedBenefitDetails, queryGroupNumber, setDashboardHeader, waivedBenefitDetails.length]);

	useEffect(() => {
		if (queryGroupNumber) {
			const fetchGroup = async () => {
				try {
					const group = await api.group.getGroupByGroupNumber(queryGroupNumber as number);
					setCurrentGroup(group);
				} catch (error) {
					console.error("Error fetching group:", error);
				}
			};

			fetchGroup();
		}
	}, [queryGroupNumber, setCurrentGroup]);

	return (
		<>
			<Suspense fallback={<div />}>
				<LazyCustomDialog dialogProps={customDialogProps} handleClose={handleCloseCustomDialogClick} />
			</Suspense>
			<Suspense fallback={<div />}>
				<LazyCustomWaivedBenifitDialog
					waivedDetails={waivedDetails}
					dialogProps={customWaivedDialogProps}
					handleTerminationDateChange={handleTerminationDateChange}
					handleTerminationReasonChange={handleTerminationReasonChange}
					waivedValidation={waivedValidation}
					handleCloseCustomWaivedDialogClick={handleCloseCustomWaivedDialogClick}
					handleApproveWaivedEnrollmentClick={handleApproveWaivedEnrollmentClick}
					currentEnrollment={currentEnrollment}
				/>
			</Suspense>

			<div className="waived-benefit-grid-container glb-tab-grid-container" id="waived-benefit-grid-container">
				<div className="ag-theme-alpine" style={{ height: gridHeight, width: "100%" }}>
					<AgGridReact
						ref={agGridRef}
						rowData={waivedBenefitDetails}
						onGridReady={handleAgGridReady}
						onFirstDataRendered={handleFirstDataRendered}
						rowSelection={"Single"}
						animateRows
						suppressColumnVirtualisation={true}
						defaultColDef={{
							filter: "agTextColumnFilter",
							floatingFilter: true,
							resizable: true
						}}
						frameworkComponents={{
							handleActionCellRender: handleActionCellRender,
							handleApproveWaivedEnrollmentActionCellRenderer:
								handleApproveWaivedEnrollmentActionCellRenderer,
							handleDateCellRenderer: handleDateCellRenderer,
							handleSSNCellRenderer: handleSSNCellRenderer
						}}
					>
						{user?.role === ROLES.super_admin &&
						((user.first_name === "Chitrak" && user.last_name === "Biswas") ||
							(user.first_name === "Anupam" && user.last_name === "Biswas") ||
							(user.first_name === "Rahul" && user.last_name === "Dutta") ||
							(user.first_name === "Suman" && user.last_name === "Dutta")) ? (
							<AgGridColumn
								field="group_number"
								headerName="Group Number"
								floatingFilterComponentParams={{ suppressFilterButton: true }}
								suppressSizeToFit
							></AgGridColumn>
						) : null}
						<AgGridColumn
							field="location_number"
							headerName="Location No."
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							suppressSizeToFit={true}
						></AgGridColumn>
						<AgGridColumn
							field="member_id"
							headerName="Employee Id"
							suppressSizeToFit
							floatingFilterComponentParams={{ suppressFilterButton: true }}
						></AgGridColumn>
						<AgGridColumn
							field="employee_name"
							headerName="Employee Name"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							suppressSizeToFit={true}
						></AgGridColumn>
						<AgGridColumn
							field="insured_SSN"
							// headerName="ALT-ID"
							// headerName="SSN"
							headerName={currentGroup?.SSN_provided_by_group ? "SSN" : "ALT-ID"}
							cellRenderer="handleSSNCellRenderer"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							suppressSizeToFit={true}
						></AgGridColumn>
						<AgGridColumn
							field="plan_code"
							headerName="Plan Code"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							suppressSizeToFit={true}
						></AgGridColumn>
						<AgGridColumn
							field="plan_name"
							headerName="Plan Name"
							suppressSizeToFit
							floatingFilterComponentParams={{ suppressFilterButton: true }}
						></AgGridColumn>
						<AgGridColumn
							field="enrollment_date"
							headerName="Waived Date"
							suppressSizeToFit
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							cellRenderer="handleDateCellRenderer"
						></AgGridColumn>
						<AgGridColumn
							field="enrollment_status"
							headerName="Status"
							filter={false}
							suppressSizeToFit
						></AgGridColumn>
						<AgGridColumn
							field="enrollment_status"
							headerName="Action"
							cellRenderer="handleApproveWaivedEnrollmentActionCellRenderer"
							cellRendererParams={{ waivedDetails }}
							filter={false}
							suppressSizeToFit={true}
						></AgGridColumn>
					</AgGridReact>
				</div>
			</div>
			{isReadyForExport ? (
				<div className="pf-action-button-container mg-top-20 mg-right-ve10" id="pf-action-button-container">
					<Button className="button-green" variant="contained" onClick={handleExportButtonClick}>
						<span className="button-label-with-icon">Export</span>
						<span>
							<ImportExportIcon className="button-icon" />
						</span>
					</Button>
				</div>
			) : null}
		</>
	);
};

export default WaivedBenefitsGrid;
